// constants
import { MONTH_INITIALS, SEASON_INITIALS } from "../../../utils/date.utils";
import { VERTICAL_MONTH_COUNT } from "../../../../constants/app.constants";

const NUMBER_OF_SEASONS = 6;

export const QUARTERS = Array(4)
  .fill(0)
  .map((_, i) => `Q${i + 1}`);

// types
export type CellValue = {
  value: string;
  colSpan: number;
};
export type HorizontalHeaders = {
  SEASONS: Array<CellValue>;
  QUARTERS: Array<CellValue>;
  MONTHS: string[];
};

export type VerticalSeasons = Array<{ value: string; rowSpan: number }>;

const DEFAULT_PREVIOUS_MONTHS = 6;

// dependency injection of date function will make this easier to test
export const calculateHeader = (submitDate: Date) => {
  const FIRST_SEASON = Math.floor(submitDate.getMonth() / 3);

  // Nike year starts June. To get January (month 0) to Q3, we need to add 7.
  // To make June Q1, we subtract 5
  const MONTH_OF_QUARTER =
    submitDate.getMonth() < 5 ? submitDate.getMonth() + 7 : submitDate.getMonth() - 5;
  const QUARTER_START = Math.floor(MONTH_OF_QUARTER / 3);

  // calculate the seasons, quarters and months, as well as the number of columns they span in the grid
  const horizontalHeaders = Array(NUMBER_OF_SEASONS)
    .fill(0)
    .map((_, i) => i)
    .reduce(
      (acc, i) => {
        const accessor = Math.floor((FIRST_SEASON + i) % 4);
        // loop around the SEASON_INITIALS to get correct season
        const seasonInitial = SEASON_INITIALS[accessor];
        // check if year has moved forward
        const year =
          (submitDate.getFullYear() + Math.floor((FIRST_SEASON + i) / 4)) % 100;
        const value = `${seasonInitial} ${year}`;

        // get months
        // index 0 goes from 0 to 2; need to add 1 to index to get 3
        const monthsEnd = (accessor + 1) * 3;
        const monthsStart = i === 0 ? submitDate.getMonth() : monthsEnd - 3;
        const months = MONTH_INITIALS.slice(monthsStart, monthsEnd).map(
          (inits) => `${inits} ${year}`,
        );

        // get quarter(s)
        const quarterAccessor = (QUARTER_START + i) % 4;
        const quarterEnd = (quarterAccessor + 1) * 3;
        const quarterStart = i === 0 ? MONTH_OF_QUARTER : quarterEnd - 3;
        const quartersTotal = acc.QUARTERS.reduce(
          (acc, curr) => acc + curr.colSpan,
          0,
        );
        const seasonsTotal = acc.SEASONS.reduce(
          (acc, curr) => acc + curr.colSpan,
          0,
        );
        const diff = quartersTotal - seasonsTotal;
        // diff will be positive if there's only one season left
        const colSpan =
          i === 5 && diff > 0 ? 3 - diff : quarterEnd - quarterStart;
        const quarterValue = {
          value: QUARTERS[quarterAccessor],
          colSpan,
        };
        const QUARTER_HEADERS =
          i === 5 && quartersTotal < seasonsTotal
            ? [
                quarterValue,
                {
                  value: QUARTERS[(quarterAccessor + 1) % 4],
                  colSpan:
                    acc.MONTHS.length -
                    acc.QUARTERS.reduce((acc, curr) => acc + curr.colSpan, 0),
                },
              ]
            : [quarterValue];
        return {
          SEASONS: [
            ...acc.SEASONS,
            { value, colSpan: monthsEnd - monthsStart },
          ],
          QUARTERS: [...acc.QUARTERS, ...QUARTER_HEADERS],
          MONTHS: [...acc.MONTHS, ...months],
        };
      },
      {
        SEASONS: [],
        QUARTERS: [],
        MONTHS: [],
      } as HorizontalHeaders,
    );

  const previousDate = new Date(submitDate);
  previousDate.setMonth(previousDate.getMonth() - 7);
  const previousMonths = Array(DEFAULT_PREVIOUS_MONTHS)
    .fill(0)
    .map((_) => {
      previousDate.setMonth(previousDate.getMonth() + 1);
      return `${MONTH_INITIALS[previousDate.getMonth()]} ${
        previousDate.getFullYear() % 100
      }`;
    });

  const dateToAdd = new Date(submitDate);
  dateToAdd.setMonth(
    dateToAdd.getMonth() + horizontalHeaders.MONTHS.length - 1,
  );
  const missingVerticalMonths = Array(
    VERTICAL_MONTH_COUNT -
      DEFAULT_PREVIOUS_MONTHS -
      horizontalHeaders.MONTHS.length,
  )
    .fill(0)
    .map((_) => {
      dateToAdd.setMonth(dateToAdd.getMonth() + 1);
      return `${MONTH_INITIALS[dateToAdd.getMonth()]} ${
        dateToAdd.getFullYear() % 100
      }`;
    });

  const PREVIOUS_VALUES = Array(2)
    .fill(0)
    .map((_, i) => i)
    .reduce(
      (acc, i) => {
        // Add 3 (4 to cycle around, then minus 1 to get previous season) to current season to ensure that it's always a positive value
        const accessor = Math.floor((FIRST_SEASON + 3 - i) % 4);
        // loop around the SEASON_INITIALS to get correct season
        const seasonInitial = SEASON_INITIALS[accessor];
        // check if year has moved backward
        const year =
          (submitDate.getFullYear() - (FIRST_SEASON - i > 0 ? 0 : 1)) % 100;
        const value = `${seasonInitial} ${year}`;
        const rowSpan = i === 0 ? 3 : horizontalHeaders.SEASONS[0].colSpan;
        // get months
        return {
          SEASONS: [{ value, rowSpan }].concat(acc.SEASONS),
        };
      },
      {
        SEASONS: [] as VerticalSeasons,
      },
    );

  const missingSeason = Array(1)
    .fill(0)
    .map(
      (_) => {
        // Add 3 (4 to cycle around, then minus 1 to get previous season) to current season to ensure that it's always a positive value
        const season = FIRST_SEASON + 6;
        const accessor = Math.floor(season % 4);
        // loop around the SEASON_INITIALS to get correct season
        const seasonInitial = SEASON_INITIALS[accessor];
        // check if year has moved backward
        const year = (submitDate.getFullYear() + (season > 7 ? 2 : 1)) % 100;
        const value = `${seasonInitial} ${year}`;
        const rowSpan = missingVerticalMonths.length;
        // get months
        return {
          value,
          rowSpan,
        };
      },
      {
        SEASONS: [] as VerticalSeasons,
      },
    );
  const verticalMonths = previousMonths
    .concat(horizontalHeaders.MONTHS)
    .concat(missingVerticalMonths);
  let verticalSeasons = PREVIOUS_VALUES.SEASONS.concat(
    horizontalHeaders.SEASONS.map((season) => ({
      value: season.value,
      rowSpan: 3,
    })),
  );

  const total = verticalSeasons.reduce((acc, val) => acc + val.rowSpan, 0);
  const expectedTotal = 24; // 8 seasons * 3 months
  if (total >= expectedTotal) {
  } else {
    verticalSeasons = verticalSeasons.concat(missingSeason);
  }
  const monthsInFirstTwoSeasons =
    horizontalHeaders.SEASONS[0].colSpan + horizontalHeaders.SEASONS[1].colSpan;
  return {
    verticalSeasons,
    verticalMonths,
    horizontalHeaders,
    monthsInFirstTwoSeasons,
    submitDate,
  };
};
